"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  type HTMLAttributes,
  type InputHTMLAttributes,
  type JSX,
  useEffect,
  useState,
} from "react";
import { useDebouncedCallback } from "use-debounce";
import SearchIcon from "@/assets/icons/search.svg";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type FilterSearchProps = HTMLAttributes<HTMLDivElement> & {
  placeholder: string;
  paramName?: string;
  className?: string;
  input?: InputHTMLAttributes<HTMLInputElement>;
  icon?: JSX.Element;
};

export const FilterSearch = ({
  placeholder,
  paramName = "search",
  className,
  input,
  icon,
  ...props
}: FilterSearchProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [param, setParam] = useState<string>(
    searchParams.get(paramName)?.toString() || "",
  );

  const handleSearch = useDebouncedCallback((search: string) => {
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set(paramName, search);
    } else {
      params.delete(paramName);
    }
    params.delete("page");

    replace(`${pathname}?${params.toString()}`);
  }, 500);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParam(e.target.value);
    handleSearch(e.target.value);
  };

  useEffect(() => {
    setParam(searchParams.get(paramName)?.toString() || "");
  }, [searchParams]);

  return (
    <div className={cn("relative", className)} {...props}>
      <Input
        className={cn("h-14 rounded-xl border-0 px-4 pr-12", input?.className)}
        type="text"
        placeholder={placeholder}
        onChange={handleInputChange}
        value={param}
        data-pw={`${paramName}-input`}
      />
      {icon ? (
        icon
      ) : (
        <SearchIcon className="absolute top-1/2 right-4 z-10 -translate-y-1/2" />
      )}
    </div>
  );
};
